import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, forwardRef, IconButton, Image, Spinner, StyleProps, Text, XOutline } from '@bonusly/design-system';

import { CustomVideoPlayer } from '../../video/custom-video-player';
import { useActionInput } from '../context';
import { AttachmentCategory, EmployeeSpotlightAttachment, ImageAttachment, LinkPreviewAttachment, SocialAttachment, VideoAttachment } from '../lib/entities';
import { deleteFileFromBrowserStorage, getMimetypeFromFilename, isVideoFile } from '../utils';

// TODO - Allow user to abort upload
const renderLoading = () => (
	<Box position="relative" borderRadius="md" w="100%" style={{ paddingBottom: '56.25%' }} overflow="hidden">
		<Box position="absolute" top="0" left="0" width="100%" height="100%" bg="gray.900" display="flex" alignItems="center" justifyContent="center">
			<Spinner color="text.interactive" borderWidth="3px" size="lg" />
		</Box>
	</Box>
);

// Media attachment display (supports images, GIFs, and videos)
export const ActionMedia = forwardRef<StyleProps, 'div'>((props, ref) => {
	const { isLoadingAttachment: isLoading, cancelVideoGeneration, attachment, removeAttachment: clearAttachmentState } = useActionInput();
	const { ...boxProps } = props;
	const [canPlay] = useState(false);
	const videoRef = useRef<HTMLVideoElement>(null);

	// Explicitly load the video when it can play to fix a bug in mobile safari where the video would not play without user interaction
	useEffect(() => {
		if (canPlay && videoRef.current) {
			videoRef.current.load();
		}
	}, [canPlay]);

	const showRemoveButton = isLoading || !!attachment;

	const removeAttachment = () => {
		let request;

		// Cancel the video generation if it is in progress but before the attachment is created

		cancelVideoGeneration && cancelVideoGeneration();
		deleteFileFromBrowserStorage();
		clearAttachmentState();
		switch (attachment?.category) {
			case AttachmentCategory.Video:
				request = fetch(`/api/v2/videos/${attachment.data.id}/discard`);
				request.then((response) => {
					if (!response.ok) {
						console.error('Failed to discard video');
					}
				});
				break;
			default:
				break;
		}
	};
	const { t } = useTranslation('action_input');

	const renderMedia = () => {
		if (!attachment) return null;
		switch (attachment.category) {
			case AttachmentCategory.Image: {
				const imageAttachment = attachment as ImageAttachment;
				return <Image borderRadius="md" objectFit="cover" src={imageAttachment.gif} h="100%" w="100%" alt={imageAttachment.altText} />;
			}
			case AttachmentCategory.Social: {
				const socialAttachment = attachment as SocialAttachment;
				return isVideoFile(socialAttachment.mp4) ? (
					<Box
						// Social videos are always muted looped and auto-played
						as="video" // <video element with chakraUI props.
						src={socialAttachment.mp4}
						borderRadius="md"
						objectFit="cover"
						h="100%"
						w="100%"
						muted
						autoPlay
						loop
						playsInline
						role="application"
						title={socialAttachment.altText}
					/>
				) : (
					<Image borderRadius="md" objectFit="cover" src={socialAttachment.mp4} h="100%" w="100%" alt={socialAttachment.altText} />
				);
			}
			case AttachmentCategory.Video: {
				const videoAttachment = attachment as VideoAttachment;
				const videoMimeType = getMimetypeFromFilename(videoAttachment.data.video_original_url);
				return (
					<Box position="relative" style={{ paddingBottom: '56.25%' }} borderRadius="md" overflow="hidden">
						<Box position="absolute" top="0" left="0" width="100%" height="100%">
							<CustomVideoPlayer
								videoId="video-attachment"
								title={videoAttachment.altText}
								poster={videoAttachment.data.video_poster_url}
								ref={videoRef}
								className="video-attachment"
								data-testid="video-attachment"
								defaultMuted={false}
								allowFullScreen={false}
							>
								{videoMimeType ? (
									<source data-target="original" src={videoAttachment.data.video_original_url} type={videoMimeType} />
								) : (
									<source data-target="original" src={videoAttachment.data.video_original_url} />
								)}
							</CustomVideoPlayer>
						</Box>
					</Box>
				);
			}
			case AttachmentCategory.EmployeeSpotlight: {
				const spotlightAttachment = attachment as EmployeeSpotlightAttachment;
				const aspectRatio =
					spotlightAttachment.width != null && spotlightAttachment.height != null ? `${spotlightAttachment.width} / ${spotlightAttachment.height}` : undefined;
				return (
					<Box
						bg="bg.surface"
						borderRadius="lg"
						border="1px solid"
						borderColor="border.default"
						overflow="hidden"
						p={3}
						pb={4}
						data-testid="employee-spotlight-attachment"
					>
						{spotlightAttachment.imageUrl && (
							<Image
								src={spotlightAttachment.imageUrl}
								width="100%"
								aspectRatio={aspectRatio}
								borderRadius="md"
								alt={spotlightAttachment.altText}
								data-testid="employee-spotlight-image"
							/>
						)}
						{spotlightAttachment.title && (
							<Text mt={4} fontWeight="semibold" fontSize="md" color="text.default" data-testid="employee-spotlight-title">
								{spotlightAttachment.title}
							</Text>
						)}
					</Box>
				);
			}
			case AttachmentCategory.LinkPreview: {
				const linkPreviewAttachment = attachment as LinkPreviewAttachment;
				return (
					<Box bg="bg.surface" borderRadius="lg" border="1px solid" borderColor="border.default" overflow="hidden" data-testid="link-preview-attachment">
						{linkPreviewAttachment.imageUrl && (
							<Box maxH="12.5rem" overflow="hidden" display="flex" justifyContent="center" bg="bg.subtle">
								<Image
									src={linkPreviewAttachment.imageUrl}
									maxW="100%"
									maxH="12.5rem"
									objectFit="contain"
									alt={linkPreviewAttachment.altText}
									data-testid="link-preview-image"
								/>
							</Box>
						)}
						<Box p={3}>
							{linkPreviewAttachment.title && (
								<Text fontWeight="semibold" fontSize="sm" color="text.default" noOfLines={2} data-testid="link-preview-title">
									{linkPreviewAttachment.title}
								</Text>
							)}
							{linkPreviewAttachment.description && (
								<Text mt={1} fontSize="xs" color="text.soft" noOfLines={2} data-testid="link-preview-description">
									{linkPreviewAttachment.description}
								</Text>
							)}
							{linkPreviewAttachment.siteName && (
								<Text mt={1} fontSize="xs" color="text.softer" data-testid="link-preview-site-name">
									{linkPreviewAttachment.siteName}
								</Text>
							)}
						</Box>
					</Box>
				);
			}
			default:
				return null;
		}
	};

	if (!isLoading && !attachment) {
		return null;
	}

	return (
		<Box pos="relative" {...boxProps} ref={ref}>
			{isLoading ? renderLoading() : renderMedia()}
			{showRemoveButton && (
				<IconButton
					boxSize={8}
					aria-label={t('remove_image_label', { name: attachment?.altText })}
					icon={<XOutline size="xs" />}
					isRound={true}
					onClick={removeAttachment}
					position="absolute"
					right={2}
					size="sm"
					top={2}
					variant="solid"
				/>
			)}
		</Box>
	);
});

ActionMedia.displayName = 'ActionMedia';
